import { useCallback, useMemo } from "react";
import { Flex, toast } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { useEntityComplianceProgram } from "~/pages/Compliance/CompliancePositionsTable/hooks";
import { useInvestorWizard } from "../context";
import { CaseInfoFields } from "./CaseInfoFields";
import { CaseInfoFormValues, schema } from "./schema";
import * as styles from "./styles_vanilla.css";
import type { WizardCaseInfo } from "../types";

export const CaseInformationStep = () => {
  const { setCaseInfo, goNext, caseInfo } = useInvestorWizard();
  const { getComplianceProgram } = useEntityComplianceProgram();

  const initialValues: CaseInfoFormValues = useMemo(
    () => ({
      creationMethod: caseInfo?.creationMethod ?? "prospect",
      prospectIdOrSubscriptionUrl: caseInfo?.prospectIdOrSubscriptionUrl ?? "",
      positionId: caseInfo?.positionId ?? "",
      clientId: caseInfo?.clientId,
      clientName: caseInfo?.clientName,
      entityId: caseInfo?.entityId,
      entityName: caseInfo?.entityName,
      programName: caseInfo?.programName ?? "",
      assignToMyself: caseInfo?.assignToMyself ?? true,
    }),
    [caseInfo],
  );

  const handleSubmit = useCallback(
    async (values: CaseInfoFormValues) => {
      let programName = values.programName || undefined;
      if (values.entityId) {
        const program = await getComplianceProgram(values.entityId, values.entityName ?? "");
        if (!program) {
          toast.error("Selected entity must have compliance program enabled.");
          return;
        }
        programName = program;
      }

      const info: WizardCaseInfo = {
        creationMethod: values.creationMethod,
        prospectIdOrSubscriptionUrl: values.prospectIdOrSubscriptionUrl || undefined,
        positionId: values.positionId || undefined,
        clientId: values.clientId,
        clientName: values.clientName,
        entityId: values.entityId,
        entityName: values.entityName,
        programName,
        assignToMyself: values.assignToMyself,
      };
      setCaseInfo(info);
      goNext();
    },
    [setCaseInfo, goNext, getComplianceProgram],
  );

  return (
    <Form<CaseInfoFormValues>
      initialValues={initialValues}
      yupValidationSchema={schema}
      onSubmit={handleSubmit}
      submitOnlyChangedValues={false}
    >
      <Flex flexDirection="column" gap={24} className={styles.stepWrapper}>
        <CaseInfoFields />
      </Flex>
    </Form>
  );
};
